// L2 · Utilidades de resolución de línea de facturación por CLAVE NATURAL (sombra, sin render).
//
// Contexto: `lineas_facturacion` (L1) es la tabla derivada con ID estable que reemplazará al índice
// posicional de los arrays `meses_facturacion` / `detalle_pagos_hardware`. En L2 corre en SOMBRA:
// se resuelve `linea_id` por clave natural y se compara contra la resolución por posición actual, sin
// cambiar ninguna superficie. NADIE escribe esta tabla desde la app (es derivada por trigger en la base).
//
// La clave natural de una línea es (negocio_id, tipo, ano, mes, tramo). Estas funciones NO tocan la DB:
// reciben las líneas ya cargadas de un negocio y devuelven la coincidencia (o null).

// Normaliza a string comparable: número o texto → "03", "2026", etc. (mes/ano viven como texto en la
// tabla y en el array; el tramo puede venir número). null/undefined → "".
const norm = (v) => (v === null || v === undefined ? "" : String(v));

/**
 * PASO 5c (reingeniería del EBITDA, 2026-10-07) — bloque del ER para una línea de
 * `negocios_carteleria` según el `tipo` de su `lineas_facturacion` (NO el `venta_tipo` del
 * puente, que para TODO `negocios_carteleria` es el mismo string — hardware OPEX y gestión
 * comparten `venta_tipo='negocios_carteleria'`, solo se distinguen por su línea). Misma regla
 * que `matchTipo` de `VincularEmitidaModal.js` (ConciliacionSII) — hardware = OPEX o CAPEX
 * (CAPEX en realidad usa `venta_tipo='negocios_carteleria_hw'`, ya Bloque B sin pasar por acá),
 * gestión = `tipo='gestion'`. Fuente ÚNICA (10.1): si esta regla cambia, cambia acá y en
 * `matchTipo` (que sigue en su propio vocabulario — `subtipo` "HW-OPEX"/"Gestión" — por diseño:
 * "sin cambiar el comportamiento de Conciliación", ver doc de cierre de PASO 5c).
 */
export const BLOQUE_POR_TIPO_LINEA_CARTELERIA = { gestion: "C", opex: "B", hardware: "B" };

/** @param {string|null} tipoLinea - `lineas_facturacion.tipo` ('gestion'|'opex'|'hardware'|'contenidos'). */
export const resolverBloqueLineaCarteleria = (tipoLinea) => BLOQUE_POR_TIPO_LINEA_CARTELERIA[tipoLinea] ?? null;

/**
 * Busca la línea de un negocio por su clave natural.
 * @param {Array<Object>} lineasDelNegocio - filas de lineas_facturacion de UN negocio.
 * @param {{tipo:string, ano:(string|number), mes:(string|number), tramo?:(string|number)}} clave
 * @returns {Object|null} la línea que matchea, o null si no existe / clave incompleta.
 */
export const resolverLinea = (lineasDelNegocio, clave) => {
  if (!Array.isArray(lineasDelNegocio) || !clave) return null;
  const { tipo, ano, mes } = clave;
  if (!tipo || !ano || !mes) return null;
  const tramo = clave.tramo === null || clave.tramo === undefined ? 1 : clave.tramo;
  const t = norm(tipo);
  const a = norm(ano);
  const m = norm(mes);
  const tr = norm(tramo);
  return (
    lineasDelNegocio.find(
      (l) => norm(l.tipo) === t && norm(l.ano) === a && norm(l.mes) === m && norm(l.tramo) === tr
    ) || null
  );
};

/**
 * Reproduce en JS la derivación de la clave natural de HARDWARE de la función SQL
 * `public.nc_lineas_derivadas` (migración 20260919_l1_lineas_facturacion.sql), para poder resolver
 * `linea_id` en sombra desde el mismo array `detalle_pagos_hardware` que ve el loader.
 *
 * ESPEJO EXACTO de la rama hardware de nc_lineas_derivadas (10.1 — misma regla, dos motores):
 *   · ano/mes salen de COALESCE(facturafecha, fecha_factura) del tramo (NO del mes de display de la
 *     grilla, que usa una cascada de estimaciones distinta). Tramo sin esa fecha → NO tiene línea (null).
 *   · tramo = row_number() OVER (PARTITION BY 'YYYY-MM' ORDER BY posición) entre los tramos DATABLES.
 * Cualquier cambio en nc_lineas_derivadas (hardware) debe replicarse aquí, y viceversa.
 *
 * @param {Array<Object>} detalle - array crudo detalle_pagos_hardware del negocio (orden = posición).
 * @param {number} entryIdx - índice 0-based del tramo dentro de `detalle` (== venta_ref_index).
 * @returns {{tipo:'hardware', ano:string, mes:string, tramo:number}|null}
 */
export const derivarClaveHardware = (detalle, entryIdx) => {
  if (!Array.isArray(detalle) || entryIdx < 0 || entryIdx >= detalle.length) return null;
  const fechaDe = (e) => e?.facturafecha || e?.fecha_factura || null;
  const objetivo = detalle[entryIdx];
  const fechaObj = fechaDe(objetivo);
  if (!fechaObj) return null; // tramo no datable → sin línea (igual que el WHERE d_fecha IS NOT NULL)
  const ano = String(fechaObj).slice(0, 4);
  const mes = String(fechaObj).slice(5, 7);
  const ym = `${ano}-${mes}`;
  // Tramo = orden dentro del mes entre los tramos datables, por posición (ORDER BY ord). Se recorre el
  // array en orden y se cuenta cuántos datables del mismo YYYY-MM hay hasta el objetivo inclusive.
  let tramo = 0;
  for (let i = 0; i < detalle.length; i++) {
    const f = fechaDe(detalle[i]);
    if (!f) continue;
    if (String(f).slice(0, 7) === ym) tramo += 1;
    if (i === entryIdx) break;
  }
  return { tipo: "hardware", ano, mes, tramo };
};

/**
 * L6 tramo 3 · Busca, en el array crudo `detalle_pagos_hardware`, el ELEMENTO cuya clave natural
 * (derivarClaveHardware) matchea la clave dada (ano, mes, tramo) — de una línea ya conocida. Reemplaza
 * el patrón "leer por `entry_index` pasado desde afuera" por una re-derivación por CONTENIDO (10.1):
 * nunca confía en una posición externa, siempre la recalcula. Usado por superficies de solo-LECTURA
 * (p.ej. `EditarFechaCobroModal`) que necesitan un campo del array que `lineas_facturacion` no trackea
 * (como `fecha_cobro_estimada`).
 * @param {Array<Object>} detalle - array crudo detalle_pagos_hardware (orden = posición).
 * @param {{ano:(string|number), mes:(string|number), tramo?:(string|number)}} clave
 * @returns {Object|null} el elemento del array que matchea, o null.
 */
export const resolverElementoHardwarePorClave = (detalle, clave) => {
  if (!Array.isArray(detalle) || !clave) return null;
  const { ano, mes } = clave;
  if (!ano || !mes) return null;
  const tramo = clave.tramo === null || clave.tramo === undefined ? 1 : clave.tramo;
  const a = norm(ano);
  const m = norm(mes);
  const tr = norm(tramo);
  for (let i = 0; i < detalle.length; i++) {
    const c = derivarClaveHardware(detalle, i);
    if (c && norm(c.ano) === a && norm(c.mes) === m && norm(c.tramo) === tr) return detalle[i];
  }
  return null;
};
